import { createReducer } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import {
    calculateModuleCompletion,
    completeTrainingTask,
    resyncTrainingProgress,
    setSelectedStep,
    setTrainingModule,
    setWatchingHelpVideo,
} from './actions';
import { firstIncompleteStep } from './sagas';
import {
    buildModule,
    internalTrainingModules,
    isCompletable,
    Module,
    TrainingModuleName,
    UnknownModuleError,
    UnknownStepError,
} from './types';

/**
 * Reducer for the teacher training module in Sparx Web which shows a teacher how to use the student
 * experience.
 */

export const TRAINING_KEY = 'training';

export type ModuleCache = { [Property in TrainingModuleName]: Module };

const buildModules = (): ModuleCache =>
    Object.keys(internalTrainingModules).reduce((acc, moduleName) => {
        acc[moduleName] = buildModule(internalTrainingModules[moduleName]);
        return acc;
    }, {} as ModuleCache);

// The TrainingState. If training is enabled then currentModule and currentStep
// must both be defined, otherwise they must both be undefined indicating that
// training is disabled.
export type TrainingState = { modules: ModuleCache; watchingHelpVideo: boolean } & (
    | {
          currentModule: undefined;
          currentStep: undefined;
      }
    | {
          currentModule: TrainingModuleName;
          currentStep: number;
      }
);

const initialState: TrainingState = {
    modules: buildModules(),
    currentModule: undefined,
    currentStep: undefined,
    watchingHelpVideo: false,
};

const isModuleComplete = (module: Module): boolean =>
    module.isComplete ||
    (!!module.steps.length &&
        module.steps.reduce((c, s) => c && (!isCompletable(s) || s.isComplete), true));

export const trainingStateReducer = createReducer<TrainingState>(initialState, builder =>
    builder
        .addCase(setTrainingModule, (state, { payload: moduleName }) => {
            if (!(moduleName in state.modules)) {
                throw new UnknownModuleError(moduleName);
            }
            state.currentModule = moduleName;
            state.currentStep = firstIncompleteStep(state.modules[moduleName].steps);
        })
        .addCase(completeTrainingTask, (state, { payload }) => {
            if (state.currentModule === undefined) {
                return;
            }

            const { moduleName, stepID } = payload;
            const stepKey = `${moduleName}/${stepID}`;
            if (!(moduleName in state.modules)) {
                throw new UnknownModuleError(moduleName);
            }
            const module = state.modules[moduleName];
            const step = module.steps[module.stepMap[stepKey]];
            if (step === undefined) {
                throw new UnknownStepError(stepKey);
            }
            if (isCompletable(step)) {
                castDraft(step).isComplete = true;
            }
        })
        .addCase(calculateModuleCompletion, (state, { payload: moduleName }) => {
            if (!(moduleName in state.modules)) {
                throw new UnknownModuleError(moduleName);
            }
            const module = state.modules[moduleName];
            module.isComplete = isModuleComplete(module);
        })
        .addCase(setWatchingHelpVideo, (state, { payload: watchingHelpVideo }) => {
            if (state.currentModule === undefined) {
                throw new Error('Must be in training mode to set the current step');
            }
            state.watchingHelpVideo = watchingHelpVideo;
        })
        .addCase(setSelectedStep, (state, { payload: selectedStep }) => {
            if (state.currentModule === undefined) {
                throw new Error('Must be in training mode to set the current step');
            }
            const currentModule = state.modules[state.currentModule];
            if (currentModule === undefined) {
                throw new UnknownModuleError(state.currentModule);
            }

            if (selectedStep < 0) {
                selectedStep = 0;
            }
            if (selectedStep >= currentModule.steps.length) {
                selectedStep = currentModule.steps.length - 1;
            }

            state.currentStep = selectedStep;
        })
        .addCase(resyncTrainingProgress, (state, { payload }) => {
            // First clear all current completion
            for (const m in state.modules) {
                const module = state.modules[m];
                module.isComplete = false;
                for (const s of module.steps) {
                    if (isCompletable(s)) {
                        castDraft(s).isComplete = false;
                    }
                }
            }
            // Then set all completion
            if (payload?.moduleData) {
                for (const m in payload.moduleData) {
                    const md = payload.moduleData[m];
                    for (const stepName of md.completedSteps) {
                        const module = state.modules[m];
                        if (module) {
                            module.isComplete = !!md.completedTime;
                            const step = module.steps[module.stepMap[stepName]];
                            if (step && isCompletable(step)) {
                                castDraft(step).isComplete = true;
                            }
                        }
                    }
                }
            }
            // Then calculate module completions
            for (const m in state.modules) {
                const module = state.modules[m];
                module.isComplete = module.isComplete || isModuleComplete(module);
            }
        }),
);
